import { useState, useEffect } from "react";
import { ArrowRight, Mail, Phone, Sparkles, EyeOff, Download, Newspaper, ExternalLink } from "lucide-react";
import Navbar from "@/components/Navbar";
import InquiryForm from "@/components/InquiryForm";
import InstagramSection from "@/components/InstagramSection";
import { fetchPressArticles } from "@/lib/api";
import {
  HERO_IMAGE,
  FORM_IMAGE,
  MOVIE,
  MEDIA_CONTACT,
  PRESS_RELEASE,
  PAST_PRESS_RELEASES,
} from "@/constants/site";

const scrollToForm = () => {
  document
    .getElementById("inquiry")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Landing() {
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [emailRevealed, setEmailRevealed] = useState(false);
  const [pressArticles, setPressArticles] = useState([]);

  useEffect(() => {
    fetchPressArticles()
      .then(setPressArticles)
      .catch(() => setPressArticles([]));
  }, []);

  return (
    <div className="min-h-screen bg-fs-navy text-fs-cream" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section
        id="hero"
        className="relative pt-16 bg-cinematic noise-overlay"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-14 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 fs-fade-up">
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-fs-orange">
                <Sparkles className="h-3.5 w-3.5" />
                A New Animated Feature
              </span>
              <h1
                className="font-display mt-4 text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] text-fs-cream"
                data-testid="hero-title"
              >
                {MOVIE.title}
                <span className="block text-gradient-gold">
                  {MOVIE.subtitle}
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-fs-silver leading-relaxed">
                An action-comedy-musical heist with the smallest crew, the
                biggest stakes, and the kind of trouble only a bee, a firefly,
                and a drama-queen spider can pull off.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="btn-fs-primary"
                  data-testid="hero-contact-cta"
                >
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href="#about"
                  className="btn-fs-ghost"
                  data-testid="hero-explore-link"
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById("about")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                >
                  About the film
                </a>
              </div>
              <div className="mt-10 text-xs uppercase tracking-[0.25em] text-fs-silver/70">
                <span>Action · Comedy · Musical · Heist</span>
              </div>
            </div>

            <div className="lg:col-span-6 fs-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative">
                <div className="absolute -inset-4 sm:-inset-6 rounded-3xl bg-gradient-to-br from-fs-orange/25 via-fs-gold/15 to-transparent blur-2xl" />
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(255,138,30,0.35)] bg-fs-navy-2">
                  <img
                    src={HERO_IMAGE}
                    alt="Fly Squad — First Strike key art"
                    className="w-full h-auto block"
                    data-testid="hero-image"
                  />
                </div>
                <div className="absolute -bottom-3 left-4 right-4 h-3 rounded-full bg-fs-orange/40 blur-xl" />
              </div>
            </div>
          </div>
        </div>
        <div className="divider-metal" />
      </section>

      {/* BLURB */}
      <section
        id="about"
        className="relative py-20 sm:py-28 bg-fs-navy-2 noise-overlay"
        data-testid="blurb-section"
      >
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-fs-orange text-center mb-5">
            The Logline
          </p>
          <blockquote
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-fs-cream leading-tight text-center"
            data-testid="blurb-logline"
          >
            <span className="text-fs-gold">“</span>
            {MOVIE.logline}
            <span className="text-fs-gold">”</span>
          </blockquote>

          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            <Stat label="Genre" value={MOVIE.genre} testid="blurb-genre" />
            <Stat
              label="Audience"
              value={MOVIE.audience}
              testid="blurb-audience"
            />
          </div>

          <div
            className="mt-14 sm:mt-16 max-w-3xl mx-auto space-y-5 text-fs-cream/85 text-base sm:text-[1.05rem] leading-relaxed"
            data-testid="blurb-about"
          >
            <p>
              Fly Squad: First Strike arrives at a moment when the world is
              paying closer attention to the rapid decline of insect populations
              and the critical role insects play in ecosystems, food security,
              biodiversity, and life on Earth.
            </p>
            <p>
              Created by Anthony Maley, a well-known former breakfast comedy
              radio host. The film uses comedy, music, action, and adventure to
              bring an urgent environmental theme to a broad family audience
              without becoming heavy-handed or preachy. At its heart, Fly Squad
              is an entertaining theatrical movie — but beneath the fun is a
              timely story about courage, friendship, survival, and protecting
              the smallest creatures that hold the world together.
            </p>
            <p>
              The project has been developed with franchise potential from the
              outset. Production conversations are already extending beyond a
              single feature film into a wider universe that may include a
              trilogy, television spin-offs, character-led extensions,
              merchandising, licensing, music, digital content, and brand
              partnerships.
            </p>
            <p>
              Fly Squad: First Strike is also part of a broader creative slate,
              with the parent company developing multiple productions across
              different stages of development. This gives the project a larger
              production foundation and positions Fly Squad not only as a
              standalone animated feature, but as the potential launch of a
              long-term entertainment property.
            </p>
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section
        id="inquiry"
        className="relative py-20 sm:py-28 bg-cinematic noise-overlay"
        data-testid="inquiry-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-fs-orange mb-3">
              Get in Touch
            </p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-fs-cream leading-[0.95]">
              General <span className="text-gradient-gold">Inquiry</span>
            </h2>
            <p className="mt-4 text-fs-silver">
              Press, partnerships, licensing, talent, investment — drop us a
              line and the right team member will reply.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
            <div className="lg:col-span-5">
              <div className="h-full rounded-2xl overflow-hidden border border-white/10 bg-fs-navy-2">
                <img
                  src={FORM_IMAGE}
                  alt="Fly Squad — IPF Detective badge"
                  className="w-full h-full object-cover block"
                  loading="lazy"
                  data-testid="inquiry-side-image"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-2xl border border-white/10 bg-fs-navy-2/70 backdrop-blur p-6 sm:p-8">
                <InquiryForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IN THE PRESS */}
      {pressArticles.length > 0 && (
      <section
        id="press"
        className="relative py-20 sm:py-24 bg-fs-navy noise-overlay"
        data-testid="press-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-fs-orange mb-3">
            As Seen In
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-fs-cream leading-none">
            In the <span className="text-gradient-gold">Press</span>
          </h2>
          <p className="mt-4 max-w-2xl text-fs-silver">
            The latest media coverage and features on Fly Squad — First Strike.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {pressArticles.map((article, i) => (
              <PressCard key={article.id || i} article={article} index={i} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* MEDIA CONTACT */}
      <section
        id="media"
        className="relative py-20 sm:py-24 bg-fs-navy-2 noise-overlay"
        data-testid="media-section"
      >
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <p className="text-xs tracking-[0.3em] uppercase text-fs-orange mb-3">
            For Journalists
          </p>
          <h2 className="font-display text-4xl sm:text-5xl text-fs-cream leading-none">
            Media <span className="text-gradient-gold">Inquiries</span>
          </h2>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT — Media Contact */}
            <div className="rounded-2xl border border-white/10 bg-fs-navy/60 p-7 sm:p-9">
              <p className="text-xs uppercase tracking-[0.25em] text-fs-silver/70">
                Media Contact
              </p>
              <p
                className="mt-2 font-display text-3xl sm:text-4xl text-fs-cream"
                data-testid="media-contact-name"
              >
                {MEDIA_CONTACT.name}
              </p>
              <p className="text-fs-silver mt-1">{MEDIA_CONTACT.role}</p>
              <div className="mt-6 flex flex-col gap-3">
                {emailRevealed ? (
                  <a
                    href={`mailto:${MEDIA_CONTACT.email}`}
                    className="inline-flex items-center gap-2 text-fs-gold hover:text-fs-orange transition-colors"
                    data-testid="media-contact-email"
                  >
                    <Mail className="h-4 w-4" />
                    {MEDIA_CONTACT.email}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEmailRevealed(true)}
                    className="inline-flex items-center gap-2 text-fs-silver hover:text-fs-gold transition-colors cursor-pointer self-start"
                    data-testid="media-contact-email-reveal"
                    aria-label="Show email address"
                  >
                    <EyeOff className="h-4 w-4" />
                    <span className="underline decoration-dotted underline-offset-4">
                      Click to reveal email
                    </span>
                  </button>
                )}
                {phoneRevealed ? (
                  <a
                    href={`tel:${MEDIA_CONTACT.phone.replace(/\s+/g, "")}`}
                    className="inline-flex items-center gap-2 text-fs-gold hover:text-fs-orange transition-colors"
                    data-testid="media-contact-phone"
                  >
                    <Phone className="h-4 w-4" />
                    {MEDIA_CONTACT.phone}
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPhoneRevealed(true)}
                    className="inline-flex items-center gap-2 text-fs-silver hover:text-fs-gold transition-colors cursor-pointer self-start"
                    data-testid="media-contact-phone-reveal"
                    aria-label="Show phone number"
                  >
                    <EyeOff className="h-4 w-4" />
                    <span className="underline decoration-dotted underline-offset-4">
                      Click to reveal phone
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT — Press Release Download */}
            <div className="rounded-2xl border border-white/10 bg-fs-navy/60 p-7 sm:p-9 flex flex-col">
              <p className="text-xs uppercase tracking-[0.25em] text-fs-silver/70">
                Press Kit
              </p>
              <p className="mt-2 font-display text-3xl sm:text-4xl text-fs-cream">
                Latest Press Release
              </p>
              <p className="text-fs-silver mt-2 text-sm leading-relaxed">
                {PRESS_RELEASE.title}
              </p>
              <a
                href={PRESS_RELEASE.url}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="btn-fs-primary mt-5 inline-flex items-center gap-2 self-start"
                data-testid="press-release-download"
              >
                <Download className="h-4 w-4" />
                Download Press Release
              </a>

              {PAST_PRESS_RELEASES.length > 0 ? (
                <div className="mt-7 pt-6 border-t border-white/10">
                  <p className="text-xs uppercase tracking-[0.25em] text-fs-silver/70 mb-3">
                    Previous Releases
                  </p>
                  <ul className="space-y-3">
                    {PAST_PRESS_RELEASES.map((pr, i) => (
                      <li key={i}>
                        <a
                          href={pr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group inline-flex items-start gap-2 text-fs-gold hover:text-fs-orange transition-colors"
                          data-testid={`past-press-release-${i}`}
                        >
                          <Download className="h-4 w-4 mt-0.5 shrink-0" />
                          <span className="text-sm leading-snug underline decoration-dotted underline-offset-4">
                            {pr.title}
                            {pr.date ? (
                              <span className="text-fs-silver/60 no-underline ml-1">
                                · {pr.date}
                              </span>
                            ) : null}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <InstagramSection />

      {/* FOOTER */}
      <footer className="py-10 bg-fs-navy border-t border-white/5" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs tracking-[0.25em] uppercase text-fs-silver/60">
            © {new Date().getFullYear()} Fly Squad — First Strike. All rights
            reserved.
          </p>
          <p className="text-xs text-fs-silver/50">
            A Pepperno Production.
          </p>
        </div>
      </footer>
    </div>
  );
}

function PressCard({ article, index }) {
  const isLink = !!article.url;
  const Wrapper = isLink ? "a" : "div";
  const linkProps = isLink
    ? { href: article.url, target: "_blank", rel: "noopener noreferrer" }
    : {};
  return (
    <Wrapper
      {...linkProps}
      className={`group rounded-2xl border border-white/10 bg-fs-navy-2/70 overflow-hidden flex flex-col ${
        isLink ? "hover:border-fs-orange/50 transition-colors" : ""
      }`}
      data-testid={`press-card-${index}`}
    >
      <div className="aspect-[16/10] bg-fs-navy-3 flex items-center justify-center overflow-hidden">
        {article.image ? (
          <img
            src={article.image}
            alt={article.publication}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <Newspaper className="h-10 w-10 text-fs-silver/30" />
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs uppercase tracking-[0.2em] text-fs-orange">
            {article.publication}
          </p>
          {article.date ? (
            <span className="text-xs text-fs-silver/60 shrink-0">
              {article.date}
            </span>
          ) : null}
        </div>
        {article.title ? (
          <p className="mt-2 text-fs-cream text-sm leading-snug flex-1">
            {article.title}
          </p>
        ) : null}
        {isLink ? (
          <span className="mt-3 inline-flex items-center gap-1.5 text-fs-gold text-sm group-hover:text-fs-orange transition-colors">
            Read article <ExternalLink className="h-3.5 w-3.5" />
          </span>
        ) : null}
      </div>
    </Wrapper>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-fs-navy/70 p-5">
      <p className="text-xs uppercase tracking-[0.25em] text-fs-orange">
        {label}
      </p>
      <p
        className="mt-2 text-lg sm:text-xl text-fs-cream font-medium"
        data-testid={testid}
      >
        {value}
      </p>
    </div>
  );
}
